// app/frontend/controllers/vehicle_map_controller.js
import { Controller } from "@hotwired/stimulus";
import L from "leaflet";

export default class extends Controller {
  static values = {
    url: String,
  };

  static targets = ["canvas", "summary", "tripRow", "legendItem"];

  connect() {
    if (!this.hasCanvasTarget) return;

    this.trips = [];
    this.geofences = [];
    this.selectedTripId = null;
    this.bounds = null;

    // Active zone kinds (empty = all visible)
    this.activeKinds = new Set();

    this._initMap();
    this._loadData();

    requestAnimationFrame(() => {
      if (this.map) this.map.invalidateSize();
    });
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.tripLayer = null;
    this.geofenceLayer = null;
    this.bounds = null;
  }

  // "Adjust view" button
  fitAll() {
    if (this.bounds && this.map) {
      this.map.fitBounds(this.bounds, { padding: [20, 20] });
    }
  }

  // Click on a trip in the list
  selectTrip(event) {
    const row = event.currentTarget;
    const id = parseInt(row.dataset.vehicleMapTripIdValue, 10);
    if (Number.isNaN(id)) return;

    // toggle: clicking twice deselects
    this.selectedTripId = this.selectedTripId === id ? null : id;

    this.tripRowTargets.forEach((r) => {
      const rowId = parseInt(r.dataset.vehicleMapTripIdValue, 10);
      r.classList.toggle("is-selected", rowId === this.selectedTripId);
    });

    this._renderTrips(); // redraw with new filter
  }

  // Click on a legend item to filter zones
  toggleKind(event) {
    const item = event.currentTarget;
    const kinds = (item.dataset.kinds || "").split(",").filter(Boolean);

    // Filter Logic:
    // If we click a group (e.g. Home/Work), we toggle its presence in activeKinds.
    // BUT we need to handle the "Group" click consistently.
    // If ANY of the group is present, we remove ALL of the group?
    // Or simpler: strictly toggle based on the first item?

    // Let's assume buttons are disjoint groups for now in UI usage.
    // If I click [Home, Work], I want to toggle both.

    const anyActive = kinds.some(k => this.activeKinds.has(k));

    kinds.forEach(k => {
      if (anyActive) {
        this.activeKinds.delete(k);
      } else {
        this.activeKinds.add(k);
      }
    });

    // Update legend UI
    // Rule: Dim opacity if (filter is active AND this item is NOT in active filter)
    const hasFilter = this.activeKinds.size > 0;

    this.legendItemTargets.forEach(el => {
      const elKinds = (el.dataset.kinds || "").split(",").filter(Boolean);
      const isSelected = elKinds.some(k => this.activeKinds.has(k));

      // If we have a filter, dim anything that isn't selected.
      // If no filter (Show All), nothing is dimmed.
      const shouldDim = hasFilter && !isSelected;

      el.classList.toggle("opacity-40", shouldDim);
    });

    this._renderGeofences();
    this._renderTrips();
  }

  // --- private --------------------------------------------------------

  _initMap() {
    this.map = L.map(this.canvasTarget, {
      center: [46.5, 2.5],
      zoom: 5,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(this.map);

    this.tripLayer = L.layerGroup().addTo(this.map);
    this.geofenceLayer = L.layerGroup().addTo(this.map);
  }

  async _loadData() {
    try {
      const res = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      this.trips = data.trips || [];
      this.geofences = data.geofences || [];

      this._renderGeofences();
      this._renderTrips();
      this._updateSummary();

      requestAnimationFrame(() => {
        if (this.map) this.map.invalidateSize();
      });
    } catch (e) {
      console.error("[vehicle_map] loadData error", e);
      if (this.hasSummaryTarget) {
        this.summaryTarget.textContent = "Impossible de charger les données de carte.";
      }
    }
  }

  _renderGeofences() {
    if (!this.geofenceLayer) return;
    this.geofenceLayer.clearLayers();

    this.bounds = null;

    this.geofences.forEach((g) => {
      if (g.lat == null || g.lon == null) return;

      // Filter by active kinds
      const kind = (g.kind || "custom").toLowerCase();
      // If activeKinds has items, check if this kind is included. 
      // If activeKinds empty, show ALL.
      if (this.activeKinds.size > 0 && !this.activeKinds.has(kind)) return;

      const color = this._colorForKind(g.kind);

      const circle = L.circle([g.lat, g.lon], {
        radius: g.radius_m || 100,
        weight: 1,
        fillOpacity: 0.08,
        color,
      }).addTo(this.geofenceLayer);

      circle.bindTooltip(`${g.name} (${g.kind || "custom"})`);

      this._extendBounds([g.lat, g.lon]);
    });
  }

  _renderTrips() {
    if (!this.tripLayer) return;
    this.tripLayer.clearLayers();

    // default: all trips
    let toRender = this.trips;

    // if a trip is selected -> render only that one
    if (this.selectedTripId) {
      toRender = this.trips.filter((t) => t.id === this.selectedTripId);
    } else {
      // otherwise filter by active zone kinds
      const hasFilter = this.activeKinds.size > 0;

      toRender = this.trips.filter(t => {
        // If no filter active -> Show All
        if (!hasFilter) return true;

        // default to "custom" or "other" if unknown
        let kind = (t.end && t.end.zone_kind) || "other";
        kind = kind.toLowerCase();

        if (this.activeKinds.has(kind)) return true;
        return false;
      });
    }

    toRender.forEach((t) => {
      const start = t.start || {};
      const end = t.end || {};

      if (
        start.lat == null ||
        start.lon == null ||
        end.lat == null ||
        end.lon == null
      ) {
        return;
      }

      const startLatLng = [start.lat, start.lon];
      const endLatLng = [end.lat, end.lon];
      const latlngs = (t.path && t.path.length > 0) ? t.path : [startLatLng, endLatLng];

      const line = L.polyline(latlngs, {
        weight: 3,
      }).addTo(this.tripLayer);

      const startMarker = L.circleMarker(startLatLng, {
        radius: 4,
        weight: 1,
        fillOpacity: 0.9,
      }).addTo(this.tripLayer);

      const endMarker = L.circleMarker(endLatLng, {
        radius: 4,
        weight: 1,
        fillOpacity: 0.9,
      }).addTo(this.tripLayer);

      const label = this._tripLabel(t);
      if (label) line.bindTooltip(label);

      startMarker.bindTooltip("Départ");
      endMarker.bindTooltip("Arrivée");

      this._extendBounds(startLatLng);
      this._extendBounds(endLatLng);
    });

    if (this.bounds) {
      this.map.fitBounds(this.bounds, { padding: [20, 20] });
    }
  }

  _updateSummary() {
    if (!this.hasSummaryTarget) return;

    if (!this.trips.length) {
      this.summaryTarget.textContent = "Aucun trajet géolocalisé pour l’instant.";
      return;
    }

    const distance = this.trips
      .map((t) => t.distance_km || 0)
      .reduce((a, b) => a + b, 0);

    this.summaryTarget.textContent = `${this.trips.length} trajets, ~${distance.toFixed(
      1
    )} km au total.`;
  }

  _extendBounds(latlng) {
    if (!this.bounds) {
      this.bounds = L.latLngBounds(latlng, latlng);
    } else {
      this.bounds.extend(latlng);
    }
  }

  _colorForKind(kind) {
    switch ((kind || "").toLowerCase()) {
      case "home":
      case "work":
        return "#22c55e";
      case "client":
        return "#38bdf8";
      default:
        return "#6b7280";
    }
  }

  _tripLabel(t) {
    const purpose = t.purpose || "";
    const distance = t.distance_km ? `${t.distance_km.toFixed(1)} km` : "";
    const bits = [purpose, distance].filter(Boolean);
    return bits.join(" · ");
  }
}
